import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { COGNITO_CONFIG, COLLEGE_GROUPS, ROLES } from '../utils/constants';
import { IS_MOCK_MODE, MOCK_USER } from '../services/mockData';

const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId,
});

export const AuthContext = createContext(null);

const STORAGE_PREFIX = 'cwc_college_';

function clearStorage() {
  localStorage.removeItem(`${STORAGE_PREFIX}id_token`);
  localStorage.removeItem(`${STORAGE_PREFIX}access_token`);
  localStorage.removeItem(`${STORAGE_PREFIX}refresh_token`);
}

function pickPrimaryRole(groups) {
  if (groups.includes(ROLES.ADMIN)) return ROLES.ADMIN;
  if (groups.includes(ROLES.FACULTY)) return ROLES.FACULTY;
  if (groups.includes(ROLES.OBSERVER)) return ROLES.OBSERVER;
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(IS_MOCK_MODE ? MOCK_USER : null);
  const [loading, setLoading] = useState(!IS_MOCK_MODE);

  const isAuthenticated = !!user;
  const isCollegeUser = !!user?.role;

  const extractUserFromSession = useCallback((session) => {
    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();
    const groups = payload['cognito:groups'] || [];
    const email = payload.email || payload.sub;
    const role = pickPrimaryRole(groups);

    localStorage.setItem(`${STORAGE_PREFIX}id_token`, idToken.getJwtToken());
    localStorage.setItem(`${STORAGE_PREFIX}access_token`, session.getAccessToken().getJwtToken());
    localStorage.setItem(`${STORAGE_PREFIX}refresh_token`, session.getRefreshToken().getToken());

    return {
      email,
      groups,
      role,
      sub: payload.sub,
      name: payload.name || payload.given_name || email,
      college_id: payload['custom:college_id'] || null,
      college_name: payload['custom:college_name'] || null,
      college_logo_url: payload['custom:college_logo_url'] || null,
      cohort_id: payload['custom:cohort_id'] || null,
      // Cognito stores custom attributes as strings; coerce to bool.
      // Granted independently of role — a college_admin is NOT automatically
      // a billing admin; the institution must designate a billing contact.
      is_billing_admin: payload['custom:billing_admin'] === 'true',
    };
  }, []);

  useEffect(() => {
    if (IS_MOCK_MODE) return; // mock user already set in initial state
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setLoading(false);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        clearStorage();
        setLoading(false);
        return;
      }
      setUser(extractUserFromSession(session));
      setLoading(false);
    });
  }, [extractUserFromSession]);

  const login = useCallback((email, password, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const userData = extractUserFromSession(session);
          const hasAccess = userData.groups.some((g) => COLLEGE_GROUPS.includes(g));
          if (!hasAccess) {
            cognitoUser.signOut();
            clearStorage();
            reject(new Error('Access denied. This account does not have college portal access.'));
            return;
          }
          if (!userData.college_id) {
            cognitoUser.signOut();
            clearStorage();
            reject(new Error('Account is not associated with an institution. Contact your CWC admin.'));
            return;
          }
          setUser(userData);
          resolve({ success: true });
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: (userAttributes) => {
          if (newPassword) {
            // Cognito rejects non-mutable / non-writable attributes if they
            // are echoed back when completing the challenge. Strip them all
            // (phone_number_verified bit us when an admin got a phone number).
            delete userAttributes.email_verified;
            delete userAttributes.email;
            delete userAttributes.phone_number_verified;
            delete userAttributes.phone_number;
            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
              onSuccess: (session) => {
                const userData = extractUserFromSession(session);
                setUser(userData);
                resolve({ success: true });
              },
              onFailure: (err) => reject(err),
            });
          } else {
            resolve({ challengeName: 'FORCE_CHANGE_PASSWORD' });
          }
        },
      });
    });
  }, [extractUserFromSession]);

  const changePassword = useCallback((oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) { reject(new Error('Not signed in')); return; }
      cognitoUser.getSession((sErr, session) => {
        if (sErr || !session?.isValid()) {
          reject(new Error('Session expired. Please sign in again.'));
          return;
        }
        cognitoUser.changePassword(oldPassword, newPassword, (err) => {
          if (err) reject(err); else resolve({ success: true });
        });
      });
    });
  }, []);

  const forgotPassword = useCallback((email) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => reject(err),
        inputVerificationCode: () => resolve({ codeSent: true }),
      });
    });
  }, []);

  const confirmForgotPassword = useCallback((email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const logout = useCallback(() => {
    if (IS_MOCK_MODE) {
      // In mock mode, "logout" just resets to null so the login screen shows.
      // Reload restores the mock user (matches reviewer expectations).
      setUser(null);
      window.location.href = '/login';
      return;
    }
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    clearStorage();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isCollegeUser,
      role: user?.role || null,
      isAdmin: user?.role === ROLES.ADMIN,
      isFaculty: user?.role === ROLES.FACULTY,
      isObserver: user?.role === ROLES.OBSERVER,
      // Independent flag — must be granted explicitly to a college_admin.
      isBillingAdmin: !!user?.is_billing_admin,
      login, logout, changePassword, forgotPassword, confirmForgotPassword, loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
